// Implements: F-UIX-016
/**
 * Whether a CSS media query matches, read synchronously and followed as the window changes.
 *
 * `useSyncExternalStore`, so the first paint already has the right answer: a layout that
 * rendered "wide" for one frame on a phone would draw the page wider than the screen before
 * correcting itself. A browser (or a test environment) without `matchMedia`, or one whose
 * accessor throws, answers "no match": every caller must treat that as the safe, narrow case.
 */

import { useSyncExternalStore } from "react";

function mediaList(query: string): MediaQueryList | null {
  try {
    return typeof window.matchMedia === "function" ? window.matchMedia(query) : null;
  } catch {
    return null;
  }
}

export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const list = mediaList(query);
      list?.addEventListener("change", onChange);
      return () => {
        list?.removeEventListener("change", onChange);
      };
    },
    () => mediaList(query)?.matches ?? false,
    () => false,
  );
}
